import * as React from 'react'
import { ReactElement, useMemo, useRef, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    ToolbarButton,
} from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'
import ProcessEmailForm, { ProcessEmailFormProps } from '../../../v2/components/Dialog/ProcessEmail/ProcessEmailForm'
import { EmailProcessingActionIconMap, EmailProcessingActionLabelMap } from '../../../v2/config/emailProcessingConfig'
import { useTranslation } from 'react-i18next'

export type ProcessEmailDialogProps = Omit<
    ProcessEmailFormProps,
    'onDismiss' | 'setSubmitForm' | 'setIsSubmitting' | 'globalErrors' | 'initialReferences'
>

export default function ProcessEmailDialog(props: ProcessEmailDialogProps): ReactElement {
    const [open, setOpen] = useState(false)
    const submitForm = useRef<() => Promise<void>>()
    const [isSubmitting, setIsSubmitting] = useState<boolean>()

    const IconComponent = EmailProcessingActionIconMap.get(props.action)
    const { t: translate } = useTranslation()

    const dialogSubject = useMemo(() => {
        return translate('email.filing.dialog.subject', {
            count: props.selection.getSelectedCount(),
            action: translate(EmailProcessingActionLabelMap.get(props.action)),
        })
    }, [props.selection.getSelectedCount(), props.action])

    const toggleDialog = (open: boolean) => {
        setOpen(open)
        props.setPreventDefaultEvent(open)
    }

    return (
        <Dialog open={open} onOpenChange={(_, data: DialogOpenChangeData) => toggleDialog(data.open)}>
            <DialogTrigger disableButtonEnhancement>
                <ToolbarButton
                    icon={<IconComponent />}
                    disabled={props.isLoading}
                    hidden={props.selection.getSelectedCount() === 0}
                    onClick={() => toggleDialog(true)}
                >
                    {translate(EmailProcessingActionLabelMap.get(props.action))}
                </ToolbarButton>
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>{dialogSubject}</DialogTitle>
                    <DialogContent>
                        <ProcessEmailForm
                            {...props}
                            onDismiss={() => toggleDialog(false)}
                            setSubmitForm={submit => (submitForm.current = submit)}
                            setIsSubmitting={submitting => setIsSubmitting(submitting)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance="transparent" icon={<DismissRegular />} onClick={() => toggleDialog(false)}>
                                {translate('action.cancel')}
                            </Button>
                        </DialogTrigger>
                        <Button appearance="primary" disabled={isSubmitting} icon={<IconComponent />} onClick={() => submitForm.current()}>
                            {translate(EmailProcessingActionLabelMap.get(props.action))}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
